// What the native Subscribe page should show once the store product fetch has
// finished. Pure so it can be unit-tested without the whole page.
//
// Background (#3969): on native the page builds its plan list from the store
// (Google Play / App Store). When the store returns NO products — e.g. the app
// was not installed from Google Play, or Play billing is unavailable on the
// device — the old code had only two effective states: show plans when the
// list was non-empty, and otherwise render nothing at all. That "nothing"
// state was a silent dead end: no plans, no Subscribe button, no message, and
// no telemetry. This helper makes the states explicit so the template and the
// error reporting both key off one rule.
export enum NativeSubscribeView {
  // still fetching prices from the store — show the spinner, decide nothing yet
  Loading = 'loading',
  // the store returned at least one purchasable product — show the plan cards
  ShowPlans = 'showPlans',
  // the store returned no products — show the "couldn't load options" message
  // instead of a blank paywall, and report the condition
  NoStoreProducts = 'noStoreProducts',
  // the user is already covered — an active subscription on another channel
  // (shown as the cross-platform "manage on the web" note) or an unlimited /
  // lifetime license (shown as the "no expiration" note). The native purchase
  // flow does not apply, so neither the plans nor the no-products message
  // should show; the page renders the relevant note instead.
  AlreadyCovered = 'alreadyCovered',
}

export interface NativeSubscribeState {
  // true only inside the Capacitor native app; web checkout is a different path
  isNativePlatform: boolean;
  // true while the initial store product fetch is in flight
  loadingPrices: boolean;
  // how many purchasable products the store returned
  productCount: number;
  // true when an active subscription lives on another channel (a web sub seen
  // inside the native app) — ionViewCanEnter renders the "manage on the web" note
  hasCrossPlatformSubscription: boolean;
  // true when the user holds an unlimited / lifetime license — the page renders
  // the "no expiration" note
  hasUnlimitedLicense: boolean;
}

// Decide the native view. Callers must already be on the native platform; on
// web this returns ShowPlans as a no-op (the web branch of the template drives
// its own PayPro / Paddle flow and never reads this).
export function resolveNativeSubscribeView(state: NativeSubscribeState): NativeSubscribeView {
  // if not native, this helper does not apply — the web flow decides on its own
  if (!state.isNativePlatform) {
    return NativeSubscribeView.ShowPlans;
  }
  // if the user is already covered by another channel's sub or an unlimited
  // license, the native purchase flow is not shown — the page renders that note
  // instead, so do not stack plans or a no-products message on top of it
  if (state.hasCrossPlatformSubscription || state.hasUnlimitedLicense) {
    return NativeSubscribeView.AlreadyCovered;
  }
  // if still fetching, do not flash the error message before products arrive
  if (state.loadingPrices) {
    return NativeSubscribeView.Loading;
  }
  // if the store gave us products, show the plans
  if (state.productCount > 0) {
    return NativeSubscribeView.ShowPlans;
  }
  // otherwise the store returned nothing — surface the actionable message
  return NativeSubscribeView.NoStoreProducts;
}
